import React, { useState } from 'react';
import { addDays, addMonths, endOfMonth, endOfWeek, format, isBefore, isSameDay, isSameMonth, startOfDay, startOfMonth, startOfWeek, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useBooking } from '../context/BookingContext';
interface AvailabilityCalendarProps {
  villaId: string;
  selectedDate: Date | null;
  onSelectDate: (date: Date) => void;
}
export const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  villaId,
  selectedDate,
  onSelectDate
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const {
    getBookingForDate
  } = useBooking();
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);
  const dates: Date[] = [];
  for (let cursor = startDate; cursor <= endDate; cursor = addDays(cursor, 1)) dates.push(cursor);
  return <section className="nv-shell" aria-label="Availability calendar" data-id="element-19" data-mp="1cnwv375sq61r">
      <div className="nv-glass rounded-md p-5 sm:p-6" data-id="element-20" data-mp="1j3mopj8ylva">
        <div className="flex items-center justify-between border-b border-white/10 pb-4" data-id="element-21" data-mp="3to67qs7ypqil">
          <div data-id="element-22" data-mp="3oxhd540zkdf8">
            <p className="nv-label text-nordvik-orange" data-id="element-23" data-mp="3ox7j95ctlm49">Live availability</p>
            <h2 className="mt-2 text-xl font-black uppercase tracking-tight text-white" data-id="element-24" data-mp="3ov4n03l2qwr3">{format(currentMonth, 'MMMM yyyy')}</h2>
          </div>
          <div className="flex gap-1" data-id="element-25" data-mp="3f0co8wfra3uq">
            <button type="button" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="nv-focus grid h-9 w-9 place-items-center border border-white/15 text-white/65 transition-colors hover:border-nordvik-orange hover:text-white" aria-label="Previous month" data-id="element-26" data-mp="3ex6xzdve4vxf">
              <ChevronLeft className="h-4 w-4" data-id="element-27" data-mp="31138fxvgpbra" />
            </button>
            <button type="button" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="nv-focus grid h-9 w-9 place-items-center border border-white/15 text-white/65 transition-colors hover:border-nordvik-orange hover:text-white" aria-label="Next month" data-id="element-28" data-mp="147i8b3f8mkgu">
              <ChevronRight className="h-4 w-4" data-id="element-29" data-mp="1n3h364dxgbc5" />
            </button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-7 border-l border-t border-white/10" data-id="element-30" data-mp="19c2jrwg3y84k">
          {Array.from({
          length: 7
        }, (_, index) => <div key={index} className="border-b border-r border-white/10 py-2 text-center text-[9px] uppercase tracking-[0.14em] text-white/40" data-id="element-31" data-mp="1ec8exmll6cre">
              {format(addDays(startOfWeek(currentMonth), index), 'EEEEE')}
            </div>)}
          {dates.map(day => {
          const booking = getBookingForDate(villaId, day);
          const isCurrentMonth = isSameMonth(day, monthStart);
          const isPast = isBefore(day, startOfDay(new Date()));
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const available = isCurrentMonth && !isPast && !booking;
          const tone = !isCurrentMonth || isPast ? 'text-white/20' : booking?.source === 'OTA' ? 'bg-nordvik-yellow/10 text-nordvik-yellow line-through decoration-nordvik-yellow' : booking ? 'bg-nordvik-orange/10 text-nordvik-orange line-through decoration-nordvik-orange' : isSelected ? 'bg-nordvik-orange text-white font-bold nv-glow-sm' : 'text-white hover:bg-white/10';
          return <button key={day.toISOString()} type="button" disabled={!available} onClick={() => available && onSelectDate(day)} className={`nv-focus aspect-square border-b border-r border-white/10 text-xs transition-colors ${tone} ${available ? 'cursor-pointer' : 'cursor-not-allowed'}`} aria-label={`${format(day, 'MMMM d, yyyy')}${booking ? `, ${booking.source === 'OTA' ? 'OTA reserved' : 'booked'}` : available ? ', available' : ''}`} data-id="element-32" data-mp="3qi14owhcxe3n">
                {format(day, 'd')}
              </button>;
        })}
        </div>

        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 border-t border-white/10 pt-4 text-[10px] uppercase tracking-[0.14em] text-white/50" data-id="element-33" data-mp="3lho7byzokvip">
          <span className="flex items-center gap-2" data-id="element-34" data-mp="3qdv2cwqjgmlv"><i className="h-2 w-2 bg-white" data-id="element-35" data-mp="3v6o6bie6nqww" /> Available</span>
          <span className="flex items-center gap-2 text-nordvik-orange" data-id="element-36" data-mp="3v890h6wl57u8"><i className="h-px w-3 bg-nordvik-orange" data-id="element-37" data-mp="323vjg8yt10i" /> Booked</span>
          <span className="flex items-center gap-2 text-nordvik-yellow" data-id="element-38" data-mp="816ymv5dwx5u"><i className="h-px w-3 bg-nordvik-yellow" data-id="element-39" data-mp="83jhrbmvpdkh" /> OTA reserved</span>
        </div>
      </div>
    </section>;
};